import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Outlet, useParams } from 'react-router-dom'

import { Group } from '@/models/group'
import {
  BulkActionSession,
  BulkJobStatus,
  GroupStoresFilters,
  OpsGroup,
  OpsGroupsListItem,
  StoreWithDeployStatus,
} from '@/models/operations/types'
import {
  beginBulkActionSession,
  clearBulkActionSession,
  clearCache,
  dismissBulkProgressModal,
  filterStoresBySearch,
  filterStoresByTab,
  getBulkActionSession,
  getBulkJob,
  getBulkRowActionStoreId,
  getGroup,
  getGroupsById,
  getGroupsList,
  isBulkRunningForGroup,
  isGroupDeployActionsLocked,
  isListAllInProgress,
  listStoreInfo,
  ListInProgressError,
  opsQueryKeys,
  resolveRequestError,
  runBulkClearCache,
  runBulkRedeploy,
  startDeployStatusPoll,
} from '@/services/operations'
import { useNotify } from '@/hooks'

import { BulkActionProgressModal } from './components/BulkActionProgressModal'

type BulkActionType = BulkActionSession['type']

export type StoreActionRequest = {
  type: 'clear-cache'
  store: StoreWithDeployStatus
  groupId: string
}

function getGroupLoadError(queryClient: QueryClient, groupId: string): string | null {
  return getGroup(queryClient, groupId)?.loadError ?? null
}

function useBulkSession(): BulkActionSession | null {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: opsQueryKeys.bulkActionSession,
    queryFn: () => getBulkActionSession(queryClient),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
  return data ?? null
}

function useBuildingGroupsPoll(groupIds: string[], isPaused: boolean): void {
  const queryClient = useQueryClient()
  const pollKey = groupIds.join(',')

  useEffect(() => {
    if (!pollKey || isPaused) return

    const ids = pollKey.split(',').filter(Boolean)
    const cancels = ids.map((groupId) =>
      startDeployStatusPoll({
        queryClient,
        groupId,
        mode: 'group-no-building',
      }),
    )

    return () => {
      cancels.forEach((cancel) => cancel())
    }
  }, [pollKey, isPaused, queryClient])
}

export type OperationsGroupsContextValue = {
  groups: OpsGroupsListItem[]
  isInitialMetadataLoading: boolean
  isMountRefreshing: boolean
  isRefreshing: boolean
  isListInProgress: boolean
  startRefresh: () => void
  refreshGroup: (groupId: string) => Promise<void>
  isGroupRefreshing: (groupId: string) => boolean
  getGroup: (groupId: string) => OpsGroup | undefined
  listStoreInfo: (groupId?: string, storeId?: string) => Promise<StoreWithDeployStatus[]>
  startBulkAction: (type: BulkActionType, stores: StoreWithDeployStatus[], groupId: string) => void
  session: BulkActionSession | null
  actionType: BulkActionType | null
  job: BulkJobStatus | undefined
  isRunning: boolean
  isBulkRunningForGroup: (groupId: string) => boolean
  getBulkRowActionStoreId: (groupId: string) => string | null
  isDeployActionsLockedForGroup: (groupId: string, buildingCount: number) => boolean
  isProgressModalVisible: (forGroupId?: string) => boolean
  dismissProgressModal: () => void
  closeProgressModal: () => void
  detailGroup: OpsGroup | null | undefined
  isDetailLoading: boolean
  detailLoadError: string | null
  detailGroupId: string
  runStoreAction: (
    request: StoreActionRequest,
    options?: { onSettled?: () => void },
  ) => void
  isStoreActionRunning: boolean
}

const OperationsGroupsContext = createContext<OperationsGroupsContextValue | null>(null)

let lastNotifiedBulkJobId: string | null = null

function createPendingBulkJob(session: BulkActionSession): BulkJobStatus {
  return {
    jobId: 'pending',
    status: 'in_progress',
    type: session.type,
    phase: 'webhooks',
    total: 0,
    completed: 0,
    failed: 0,
    errors: [],
  }
}

function resolveActiveBulkJob(
  session: BulkActionSession | null,
  cachedJob: BulkJobStatus | undefined,
): BulkJobStatus | undefined {
  if (!session) return undefined
  if (session.jobId === 'pending') return createPendingBulkJob(session)
  if (cachedJob?.jobId === session.jobId) return cachedJob
  return undefined
}

function OperationsGroupsProviderInner({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { notifySuccess, notifyError } = useNotify()
  const { groupId: routeGroupId } = useParams()
  const session = useBulkSession()
  const [isMountRefreshing, setIsMountRefreshing] = useState(true)
  const prevRouteGroupIdRef = useRef<string | undefined>(routeGroupId)

  const groupsCacheQuery = useQuery({
    queryKey: opsQueryKeys.groups,
    queryFn: () => getGroupsById(queryClient),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const groups = useMemo(
    () => getGroupsList(queryClient),
    [groupsCacheQuery.data, queryClient],
  )

  const detailLoadError = routeGroupId ? getGroupLoadError(queryClient, routeGroupId) : null

  const detailGroup =
    routeGroupId && !detailLoadError ? getGroup(queryClient, routeGroupId) ?? null : null

  useEffect(() => {
    if (!routeGroupId || detailLoadError) return
    const cached = getGroup(queryClient, routeGroupId)
    if (cached && (cached.stores.length > 0 || cached.loadError)) return
    if (isListAllInProgress()) return

    void listStoreInfo(queryClient, routeGroupId)
  }, [routeGroupId, detailLoadError, queryClient, groupsCacheQuery.data])

  useEffect(() => {
    const prevRouteGroupId = prevRouteGroupIdRef.current
    prevRouteGroupIdRef.current = routeGroupId

    const returnedToDashboard = Boolean(prevRouteGroupId) && !routeGroupId
    if (!returnedToDashboard) return
    if (isListAllInProgress()) return
    if (getBulkActionSession(queryClient)?.status === 'in_progress') return

    void listStoreInfo(queryClient)
  }, [routeGroupId, queryClient])

  const listAllMutation = useMutation({
    mutationFn: () => listStoreInfo(queryClient),
    onError: (error) => {
      if (error instanceof ListInProgressError) {
        notifyError('A status check is already in progress')
        return
      }
      notifyError(error instanceof Error ? error.message : 'Status check failed')
    },
  })

  const isRefreshing = listAllMutation.isPending

  const startRefresh = useCallback(() => {
    if (isListAllInProgress()) {
      notifyError('A status check is already in progress')
      return
    }
    listAllMutation.mutate()
  }, [listAllMutation, notifyError])

  const refreshGroup = useCallback(
    async (groupId: string) => {
      if (isListAllInProgress()) {
        notifyError('A full status check is already in progress')
        return
      }

      const group = getGroup(queryClient, groupId)
      if (group?.isRefreshing) return

      try {
        await listStoreInfo(queryClient, groupId)
      } catch (error) {
        notifyError(resolveRequestError(error, 'Failed to check group status'))
      }
    },
    [notifyError, queryClient],
  )

  const isGroupRefreshing = useCallback(
    (groupId: string) => getGroup(queryClient, groupId)?.isRefreshing ?? false,
    [queryClient, groupsCacheQuery.data],
  )

  useEffect(() => {
    if (getBulkActionSession(queryClient)?.status === 'in_progress') {
      setIsMountRefreshing(false)
      return
    }

    void listStoreInfo(queryClient)
      .catch(() => {
        // ListInProgressError: another list already running (e.g. strict mode).
      })
      .finally(() => {
        setIsMountRefreshing(false)
      })
  }, [queryClient])

  const isInitialMetadataLoading =
    groups.length === 0 && (groupsCacheQuery.isPending || isMountRefreshing)

  const isListInProgress =
    isRefreshing || isMountRefreshing || groups.some((group) => group.isRefreshing)

  const bulkGroupId =
    session?.status === 'in_progress' && session.type === 'redeploy' ? session.groupId : null

  const dashboardBuildingGroupIds = useMemo(
    () =>
      groups
        .filter(
          (g) =>
            !g.loadError &&
            !g.isRefreshing &&
            g.summary.building > 0 &&
            g.id !== bulkGroupId,
        )
        .map((g) => g.id),
    [groups, bulkGroupId],
  )

  const routeGroup = detailGroup ?? undefined
  const detailBuildingGroupIds =
    routeGroupId && (routeGroup?.summary.building ?? 0) > 0 ? [routeGroupId] : []

  const isBuildingPollPaused =
    isListInProgress || session?.status === 'in_progress'

  const isDetailPollPaused =
    (routeGroup?.isRefreshing ?? false) ||
    isMountRefreshing ||
    isRefreshing ||
    session?.status === 'in_progress'

  useBuildingGroupsPoll(dashboardBuildingGroupIds, isBuildingPollPaused)
  useBuildingGroupsPoll(detailBuildingGroupIds, isDetailPollPaused)

  const jobQuery = useQuery({
    queryKey: opsQueryKeys.bulkJob(session?.jobId ?? 'idle'),
    queryFn: () => getBulkJob(queryClient, session!.jobId),
    enabled: !!session?.jobId && session.jobId !== 'pending',
    structuralSharing: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const job = resolveActiveBulkJob(session, jobQuery.data)

  const mutation = useMutation({
    mutationFn: async ({
      type,
      stores,
      groupId,
    }: {
      type: BulkActionType
      stores: StoreWithDeployStatus[]
      groupId: string
    }) => {
      if (type === 'redeploy') {
        return runBulkRedeploy(queryClient, stores, groupId)
      }
      return runBulkClearCache(queryClient, stores, groupId)
    },
    onError: (error, variables) => {
      clearBulkActionSession(queryClient)
      if (variables.type === 'redeploy') {
        void listStoreInfo(queryClient, variables.groupId, undefined, { silent: true })
      }
      notifyError(error instanceof Error ? error.message : 'Bulk action failed')
    },
  })

  const startBulkAction = useCallback(
    (type: BulkActionType, stores: StoreWithDeployStatus[], groupId: string) => {
      const activeStores = stores.filter((store) => store.isActive)
      if (activeStores.length === 0) {
        notifyError('No active stores to run this action')
        return
      }

      const existing = getBulkActionSession(queryClient)
      if (mutation.isPending) {
        notifyError('A bulk action is already in progress')
        return
      }

      if (existing?.status === 'in_progress') {
        if (existing.jobId !== 'pending') {
          notifyError('A bulk action is already in progress')
          return
        }
        clearBulkActionSession(queryClient)
      } else if (existing?.status === 'completed') {
        clearBulkActionSession(queryClient)
      }

      beginBulkActionSession(
        queryClient,
        groupId,
        type,
        activeStores.map((store) => store.id),
      )
      mutation.mutate({ type, stores: activeStores, groupId })
    },
    [mutation, notifyError, queryClient],
  )

  const storeActionMutation = useMutation({
    mutationFn: async ({ store }: StoreActionRequest) => {
      const result = await clearCache([store], store.id)

      if (result.failed > 0) {
        throw new Error(result.errors[0]?.message ?? 'Action failed')
      }
    },
    onSuccess: () => {
      notifySuccess('Purge cache triggered for store')
    },
    onError: (error) => {
      notifyError(error instanceof Error ? error.message : 'Action failed')
    },
  })

  const runStoreAction = useCallback(
    (request: StoreActionRequest, options?: { onSettled?: () => void }) => {
      storeActionMutation.mutate(request, { onSettled: options?.onSettled })
    },
    [storeActionMutation],
  )

  useEffect(() => {
    const activeSession = getBulkActionSession(queryClient)
    if (!job || job.status !== 'completed' || job.jobId !== activeSession?.jobId) return
    if (lastNotifiedBulkJobId === job.jobId) return

    lastNotifiedBulkJobId = job.jobId
    const actionLabel = job.type === 'redeploy' ? 'Publish' : 'Purge Cache'
    if (job.failed > 0) {
      notifyError(`${actionLabel}: ${job.completed}/${job.total} completed, ${job.failed} failed`)
    } else {
      notifySuccess(`${actionLabel}: ${job.completed}/${job.total} completed successfully`)
    }

    if (activeSession?.groupId && job.type === 'redeploy') {
      void listStoreInfo(queryClient, activeSession.groupId, undefined, { silent: true })
    }

    if (activeSession?.progressModalDismissed) {
      clearBulkActionSession(queryClient)
    }
  }, [job, notifyError, notifySuccess, queryClient])

  const getGroupFromCache = useCallback(
    (groupId: string) => getGroup(queryClient, groupId),
    [queryClient],
  )

  const listStoreInfoForGroup = useCallback(
    (groupId?: string, storeId?: string) => listStoreInfo(queryClient, groupId, storeId),
    [queryClient],
  )

  const isBulkRunningForGroupId = useCallback(
    (groupId: string) => isBulkRunningForGroup(queryClient, groupId),
    [queryClient, session],
  )

  const getBulkRowActionStoreIdForGroup = useCallback(
    (groupId: string) => getBulkRowActionStoreId(queryClient, groupId),
    [queryClient, session],
  )

  const isDeployActionsLockedForGroup = useCallback(
    (groupId: string, buildingCount: number) =>
      isGroupDeployActionsLocked(queryClient, groupId, buildingCount),
    [queryClient, session],
  )

  const isRunning =
    session?.status === 'in_progress' ||
    mutation.isPending ||
    job?.status === 'in_progress'

  const isProgressModalVisible = useCallback(
    (forGroupId?: string) => {
      if (!session || session.progressModalDismissed) return false
      if (session.status !== 'in_progress' && session.status !== 'completed') return false
      if (forGroupId !== undefined && session.groupId !== forGroupId) return false
      return true
    },
    [session],
  )

  const dismissProgressModal = useCallback(() => {
    dismissBulkProgressModal(queryClient)
  }, [queryClient])

  const closeProgressModal = useCallback(() => {
    clearBulkActionSession(queryClient)
  }, [queryClient])

  const value: OperationsGroupsContextValue = {
    groups,
    isInitialMetadataLoading,
    isMountRefreshing,
    isRefreshing,
    isListInProgress,
    startRefresh,
    refreshGroup,
    isGroupRefreshing,
    getGroup: getGroupFromCache,
    listStoreInfo: listStoreInfoForGroup,
    startBulkAction,
    session,
    actionType: session?.type ?? null,
    job,
    isRunning,
    isBulkRunningForGroup: isBulkRunningForGroupId,
    getBulkRowActionStoreId: getBulkRowActionStoreIdForGroup,
    isDeployActionsLockedForGroup,
    isProgressModalVisible,
    dismissProgressModal,
    closeProgressModal,
    detailGroup,
    isDetailLoading: Boolean(
      routeGroupId &&
        !detailLoadError &&
        (detailGroup?.isRefreshing ||
          (!detailGroup?.stores.length && detailGroup?.loadError == null)),
    ),
    detailLoadError,
    detailGroupId: routeGroupId ?? '',
    runStoreAction,
    isStoreActionRunning: storeActionMutation.isPending,
  }

  return (
    <OperationsGroupsContext.Provider value={value}>{children}</OperationsGroupsContext.Provider>
  )
}

export const OperationsGroupsLayout = () => {
  const { groupId } = useParams()

  return (
    <OperationsGroupsProviderInner>
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        <Outlet />
      </div>
      <BulkProgressModalGate groupId={groupId} />
    </OperationsGroupsProviderInner>
  )
}

function BulkProgressModalGate({ groupId }: { groupId?: string }) {
  const { job, isProgressModalVisible, dismissProgressModal, closeProgressModal } =
    useOperationsGroups()

  return (
    <BulkActionProgressModal
      visible={isProgressModalVisible(groupId)}
      job={job}
      onDismiss={dismissProgressModal}
      onCloseComplete={closeProgressModal}
    />
  )
}

export function useOperationsGroups(): OperationsGroupsContextValue {
  const ctx = useContext(OperationsGroupsContext)
  if (!ctx) {
    throw new Error('useOperationsGroups must be used within OperationsGroupsLayout')
  }
  return ctx
}

export function useGroupDetailStores(initialStatus: 'active' | 'churned' = 'active') {
  const {
    detailGroup,
    isDetailLoading,
    detailLoadError,
    detailGroupId,
    runStoreAction,
    isStoreActionRunning,
  } = useOperationsGroups()

  const [filters, setFilters] = useState<Pick<GroupStoresFilters, 'status' | 'search'>>({
    status: initialStatus,
    search: '',
  })

  const stores = useMemo(() => {
    const allStores = detailGroup?.stores ?? []
    const byTab = filterStoresByTab(allStores, filters.status)
    return filterStoresBySearch(byTab, filters.search)
  }, [detailGroup?.stores, filters])

  const setStatus = (status: 'active' | 'churned') => {
    setFilters((prev) => ({ ...prev, status }))
  }

  const setSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }))
  }

  return {
    group: detailGroup ?? undefined,
    isLoading: isDetailLoading,
    groupId: detailGroupId,
    loadError: detailLoadError,
    filters,
    stores,
    setStatus,
    setSearch,
    activeCount: detailGroup?.summary.active ?? 0,
    churnedCount: detailGroup?.summary.churned ?? 0,
    runStoreAction,
    isStoreActionRunning,
  }
}
